import { useState, useCallback, useEffect } from "react";
import Grid from "@mui/material/Grid";
import Paper from "@mui/material/Paper";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import HelpIcon from "@mui/icons-material/Help";
import StarIcon from "@mui/icons-material/Star";
import { TextField } from "@/components/molecules/TextField";
import { Button } from "@/components/molecules/Button";
import { useNavigate, useLocation } from "react-router-dom";
import {
  LoginConstants,
  LoginModel,
  LoginModelErrorType,
  CookieKey,
} from "@/models/auth";
import { Title as CommonTitle } from "@/common/CommonConstants";
import { loginAPI } from "@/util/api/API";
import Cookies from "js-cookie";
import { useLoadingPromise } from "@/hooks/useLoadingPromise";

export const Login = (): JSX.Element => {
  const [userLogin, setUserLogin] = useState<LoginModel>({
    id: "",
    password: "",
  });
  const [errorData, setErrorData] = useState<LoginModelErrorType>({});
  const navigate = useNavigate();
  const location = useLocation();

  const [loading, loginAPIWrapper] = useLoadingPromise(loginAPI);

  // Handle event change data
  const onChangeId = useCallback(
    (value: string) => {
      setUserLogin({ ...userLogin, id: value });
      setErrorData({ ...errorData, id: false });
    },
    [userLogin, errorData]
  );

  const onChangePassword = useCallback(
    (value: string) => {
      setUserLogin({ ...userLogin, password: value });
      setErrorData({ ...errorData, password: false });
    },
    [userLogin, errorData]
  );

  const handleLogin = useCallback(async () => {
    if (loading) {
      return;
    }

    try {
      const errorDataTemp: LoginModelErrorType = {
        id: !userLogin.id,
        password: !userLogin.password,
      };

      // 入力値チェック
      if (Object.values(errorDataTemp).some((it) => it)) {
        setErrorData({ ...errorDataTemp });
        return;
      } else {
        setErrorData({});
      }

      const res = await loginAPIWrapper({
        ...userLogin,
      });
      Cookies.set(CookieKey.gxoperator, res.data.token);

      // URLパラメータから遷移元を取得
      const searchParams = new URLSearchParams(location.search);
      const redirectTo = searchParams.get('redirect');

      // 遷移元が存在し、有効なパスの場合はそこに遷移、そうでなければダッシュボードに遷移
      if (redirectTo?.startsWith('/') && redirectTo !== '/login') {
        navigate(redirectTo);
      } else {
        navigate("/dashboard");
      }
    } catch (err) {
      console.log("==Error when login api==", err);
    }
  }, [loading, userLogin, loginAPIWrapper, navigate, location.search]);

  useEffect(() => {
    document.title = "ログイン｜" + CommonTitle;
  }, []);

  return (
    <Box
      sx={{
        height: "100vh",
        backgroundColor: "#f5f5f5",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        px: 3,
      }}
    >
      <Grid container spacing={6} sx={{ maxWidth: 1200, mx: 'auto' }}>
        {/* 左側：ログインフォーム */}
        <Grid item xs={12} md={5}>
          <Paper
            sx={{
              p: 5,
              display: "flex",
              flexDirection: "column",
              borderRadius: 4,
              boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
            }}
          >
            {/* ロゴ */}
            <Box sx={{ textAlign: "center", mb: 3 }}>
              <Box
                component="img"
                src="/gx_logo.png"
                alt="GO Logo"
                sx={{
                  width: "auto",
                  height: 80,
                  mb: 2,
                }}
              />
              <Typography
                sx={{
                  fontSize: 20,
                  fontWeight: "bold",
                  color: "#333",
                }}
              >
                充電サービス管理画面
              </Typography>
            </Box>

            {/* フォーム */}
            <Box sx={{ mt: 2 }}>
              <Box sx={{ mb: 3 }}>
                <TextField
                  label={LoginConstants.LoginId}
                  value={userLogin?.id}
                  setValue={(value) => onChangeId(value)}
                  isRequired
                  hasValueError={errorData.id}
                  errorLabel={LoginConstants.LoginId}
                  shrinkOnAutoFill
                />
              </Box>
              <Box sx={{ mb: 4 }}>
                <TextField
                  type="password"
                  label={LoginConstants.Password}
                  value={userLogin?.password}
                  setValue={(value) => onChangePassword(value)}
                  isRequired
                  hasValueError={errorData.password}
                  errorLabel={LoginConstants.Password}
                  shrinkOnAutoFill
                />
              </Box>
              <Button
                label={LoginConstants.Button}
                sx={{
                  backgroundColor: "#4CAF50",
                  width: "100%",
                  py: 1.5,
                  fontSize: 16,
                  "&:hover": {
                    backgroundColor: "#45a049",
                  },
                }}
                onClick={() => handleLogin()}
                disabled={loading}
              />
            </Box>
          </Paper>
        </Grid>

        {/* 右側：説明テキスト */}
        <Grid item xs={12} md={6}>
          <Box sx={{ color: "#333", pt: 7 }}>
            <Typography
              sx={{
                fontSize: 24,
                fontWeight: "bold",
                mb: 3,
                color: "#4CAF50",
              }}
            >
              このページは、
              <br />
              EVタクシープロジェクトに関する管理画面です
            </Typography>

            <Box sx={{ mb: 3 }}>
              <Box sx={{ display: "flex", alignItems: "flex-center", mb: 1 }}>
                <HelpIcon
                  sx={{
                    fontSize: 24,
                    color: "#666",
                    mr: 1,
                    flexShrink: 0,
                  }}
                />
                <Typography sx={{ fontSize: 16, fontWeight: "bold" }}>
                  ログインIDやパスワードがご不明な場合
                </Typography>
              </Box>
              <Box sx={{ ml: 1 }}>
                <Typography sx={{ fontSize: 14, mb: 1, lineHeight: 1.8 }}>
                  • GO管理画面にログインする際のログインIDとパスワードをご入力ください。
                </Typography>
                <Typography sx={{ fontSize: 14, mb: 1, lineHeight: 1.8 }}>
                  • ログインIDとパスワードが不明な場合、GOタクシーサポートへお問合せください。
                </Typography>
                <Box sx={{ ml: 2, mt: 1 }}>
                  <Typography sx={{ fontSize: 14, lineHeight: 1.8 }}>
                    • JAPAN TAXI 業務支援（JTX構成）：050-3183-8895
                  </Typography>
                  <Typography sx={{ fontSize: 14, lineHeight: 1.8 }}>
                    • タクシーモニター（MOV構成）：050-3131-1565
                  </Typography>
                </Box>
              </Box>
            </Box>

            <Box>
              <Box sx={{ display: "flex", alignItems: "flex-center", mb: 1 }}>
                <StarIcon
                  sx={{
                    fontSize: 24,
                    color: "#666",
                    mr: 1,
                    flexShrink: 0,
                  }}
                />
                <Typography sx={{ fontSize: 16, fontWeight: "bold" }}>
                  ブックマーク（お気に入り）の登録をお願いします
                </Typography>
              </Box>
              <Box sx={{ ml: 1 }}>
                <Typography sx={{ fontSize: 14, lineHeight: 1.8 }}>
                  • ログイン管理画面URL（https://gx.monitor.m-o-v.jp/）でブックマークを登録してご利用ください。
                </Typography>
              </Box>
            </Box>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};
